import { SmtpServer } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Circle, Clock9, Mail } from 'lucide-react';
import React from 'react';

// The Email interface should match the one in UnifiedInboxBasePage.
export interface Email {
  id: string;
  threadId?: string | null;
  from: string;
  subject: string;
  date: string;
  body: string;
  read: boolean;
  smtpServer?: SmtpServer;
  messages?: Email[];
  nodeId?: string;
  workflowId?: string;
  organizationId?: string;
}

export interface EmailListProps {
  emails: Email[];
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onSelectEmail: (emailId: string) => void;
  selectedEmailId: string | null;
  onOpenEmailThreadDialog: (opn: boolean) => void;
}

const EmailList: React.FC<EmailListProps> = ({
  emails,
  searchTerm,
  onSearchTermChange,
  onSelectEmail,
  selectedEmailId,
  onOpenEmailThreadDialog,
}) => {
  console.log('emails', emails);
  return (
    <div className="flex-1 space-y-3 overflow-y-auto">
      {emails.length < 1 ? (
        <div className="flex h-full items-center justify-center text-sm text-gray-400">
          No emails found
        </div>
      ) : (
        emails.map(email => (
          email.messages && email.messages.length > 1 && (
            <Card
              key={email.id}
              onClick={() => {
                onSelectEmail(email.id);
                onOpenEmailThreadDialog(true);
              }}
              className={`relative cursor-pointer hover:bg-gray-50 ${
                selectedEmailId === email.id ? 'bg-blue-100' : 'bg-white'
              } rounded-md shadow-none`}
            >
              <CardContent className="p-3 pl-6">
                <div className="flex w-full items-start">
                  <div className="mr-2">
                    <Mail className="mt-2 h-5 w-5 text-blue-500" />
                  </div>
                  <div className="w-full">
                    <div className="mt-1 flex items-center justify-between text-base">
                      {email.subject}

                      <div className="mt-1 flex items-center space-x-1 text-xs text-gray-600">
                        {email.smtpServer && email.smtpServer === 'OUTLOOK' ? (
                          <div className="flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-2 h-7 w-7"
                              viewBox="0 0 48 48"
                            >
                              <path
                                fill="#03A9F4"
                                d="M21,31c0,1.104,0.896,2,2,2h17c1.104,0,2-0.896,2-2V16c0-1.104-0.896-2-2-2H23c-1.104,0-2,0.896-2,2V31z"
                              ></path>
                              <path
                                fill="#B3E5FC"
                                d="M42,16.975V16c0-0.428-0.137-0.823-0.367-1.148l-11.264,6.932l-7.542-4.656L22.125,19l8.459,5L42,16.975z"
                              ></path>
                              <path
                                fill="#0277BD"
                                d="M27 41.46L6 37.46 6 9.46 27 5.46z"
                              ></path>
                              <path
                                fill="#FFF"
                                d="M21.216,18.311c-1.098-1.275-2.546-1.913-4.328-1.913c-1.892,0-3.408,0.669-4.554,2.003c-1.144,1.337-1.719,3.088-1.719,5.246c0,2.045,0.564,3.714,1.69,4.986c1.126,1.273,2.592,1.91,4.378,1.91c1.84,0,3.331-0.652,4.474-1.975c1.143-1.313,1.712-3.043,1.712-5.199C22.869,21.281,22.318,19.595,21.216,18.311z M19.049,26.735c-0.568,0.769-1.339,1.152-2.313,1.152c-0.939,0-1.699-0.394-2.285-1.187c-0.581-0.785-0.87-1.861-0.87-3.211c0-1.336,0.289-2.414,0.87-3.225c0.586-0.81,1.368-1.211,2.355-1.211c0.962,0,1.718,0.393,2.267,1.178c0.555,0.795,0.833,1.895,0.833,3.31C19.907,24.906,19.618,25.968,19.049,26.735z"
                              ></path>
                            </svg>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <svg
                              aria-hidden="true"
                              className="mr-2 h-6 w-6"
                              viewBox="0 0 48 48"
                            >
                              <path
                                fill="#FFC107"
                                d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                              ></path>
                              <path
                                fill="#FF3D00"
                                d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                              ></path>
                              <path
                                fill="#4CAF50"
                                d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                              ></path>
                              <path
                                fill="#1976D2"
                                d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                              ></path>
                            </svg>
                          </div>
                        )}
                        {/* {email.read ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Circle className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                        )}
                        <span>{email.read ? 'Read' : 'Unread'}</span> */}
                      </div>
                    </div>

                    <div className="mt-1 w-[400px] truncate text-sm">
                      {email.body}
                    </div>

                    <div className="mt-1 flex items-center text-xs text-gray-500">
                      <Clock9 className="mr-1 h-3 w-3" />
                      {new Date(email.date).toLocaleDateString()}{' '}
                      {new Date(email.date).toLocaleTimeString()}
                    </div>

                    <div className="mt-1 flex items-center text-xs text-gray-500">
                      From {email.from}
                    </div>

                    {email.messages && email.messages.length > 1 && (
                      <div className="mt-1 text-xs text-gray-500">
                        {email.messages.length} messages
                      </div>
                    )}
                  </div>
                </div>

                {/* <div className="mt-2 text-xs text-gray-500 line-clamp-2">{email.body}</div> */}
              </CardContent>
            </Card>
          )
        ))
      )}
    </div>
  );
};

export default EmailList;
